/* covered.ro — Device picker: accessible brand tabs + instant model search. */
(() => {
  if (customElements.get('cv-device-picker')) return;

  const STORE_BRAND = 'cv:picker-brand';
  const STORE_DEVICE = 'cv:device';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Matches the CSS: from 768px the "more" button is a tile in the grid.
  const tileMode = window.matchMedia('(min-width: 768px)');

  const store = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (e) {
        /* storage unavailable: ignore */
      }
    },
  };

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\+/g, ' plus ')
      .replace(/[^a-z0-9 ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const compact = (value) => value.replace(/\s+/g, '');

  // Romanian plural: "20 de modele", "101 modele", "120 de modele".
  const countText = (count) => {
    const lastTwo = count % 100;
    return count >= 20 && (lastTwo === 0 || lastTwo >= 20) ? `${count} de` : String(count);
  };

  const TEXT_ENTRY = 'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, select';

  class CvDevicePicker extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = this.tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`));
      this.tablist = this.querySelector('[role="tablist"]');
      this.indicator = this.querySelector('.cv-picker__indicator');
      this.form = this.querySelector('.cv-picker__search');
      this.input = this.querySelector('.cv-picker__input');
      this.clearBtn = this.querySelector('[data-clear]');
      this.results = this.querySelector('[data-results]');
      this.resultsList = this.querySelector('[data-results-list]');
      this.empty = this.querySelector('[data-empty]');
      this.emptyBrand = this.querySelector('[data-empty-brand]');
      this.emptyBrandLabel = this.querySelector('[data-empty-brand-label]');
      this.status = this.querySelector('.cv-picker__status');
      this.current = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));

      this.index = [];
      this.panels.forEach((panel, panelIndex) => {
        if (!panel) return;
        // Brands without a model prefix ("Alt telefon") list makers, not models:
        // any model of that maker is supported, so a query naming the maker matches.
        const anyModel = panel.hasAttribute('data-any-model');
        panel.querySelectorAll('.cv-picker__model').forEach((item) => {
          const text = normalize(item.dataset.search);
          this.index.push({ item, panelIndex, text, flat: compact(text), words: text.split(' '), anyModel, clone: null });
        });
      });

      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInput = this.onInput.bind(this);

      // Sleek's theme.js (touch devices) cancels every `touchend` on the document and
      // replays a click only on links/buttons, so a tap never focuses a text field and
      // the keyboard never opens. Keep taps on our search field away from that handler
      // so the browser focuses it natively (caret where tapped, keyboard opens).
      this.addEventListener(
        'touchend',
        (event) => {
          if (event.target.closest && event.target.closest(TEXT_ENTRY)) event.stopPropagation();
        },
        { passive: true }
      );

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i, { focus: false, clearSearch: true }));
      });
      if (this.tablist) this.tablist.addEventListener('keydown', this.onKeydown);

      if (this.input) {
        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.input.value) {
            event.preventDefault();
            this.clearSearch(true);
          }
        });
      }
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          const links = this.resultsList ? this.resultsList.querySelectorAll('a[href]') : [];
          if (links.length === 1) window.location.href = links[0].href;
          else if (links.length > 1) links[0].focus();
        });
      }
      if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.clearSearch(true));

      this.addEventListener('click', (event) => {
        const chip = event.target.closest('.cv-picker__chip[data-model]');
        if (!chip) return;
        store.set(STORE_DEVICE, JSON.stringify({ brand: chip.dataset.brand, model: chip.dataset.model }));
      });

      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('[role="tabpanel"]');
        const i = this.panels.indexOf(panel);
        if (i > -1) {
          this.clearSearch(false);
          this.select(i, { focus: false, animate: false });
        }
      });

      this.querySelectorAll('[data-more]').forEach((button) => {
        button.addEventListener('click', () => this.expand(button.closest('[role="tabpanel"]')));
      });

      window.addEventListener('resize', this.onResize, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.moveIndicator());

      // Restore the last brand this visitor looked at (per-browser convenience).
      const remembered = store.get(STORE_BRAND);
      const rememberedIndex = remembered ? this.tabs.findIndex((tab) => tab.dataset.brand === remembered) : -1;
      this.select(rememberedIndex > -1 ? rememberedIndex : this.current, { focus: false, animate: false, remember: false });

      this.classList.add('is-ready');
      requestAnimationFrame(() => this.moveIndicator());
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    onResize() {
      if (this.resizeFrame) return;
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.moveIndicator();
        this.updateMore(this.panels[this.current]);
      });
    }

    /* Long lists show their first rows (CSS decides how many per breakpoint);
       the "Arată încă N modele" button counts what is hidden right now. */
    updateMore(panel) {
      if (!panel || panel.hidden) return;
      const button = panel.querySelector('[data-more]');
      if (!button) return;
      if (panel.dataset.moreAuto === 'true') {
        panel.classList.remove('is-expanded');
        delete panel.dataset.moreAuto;
      }
      const items = panel.querySelectorAll('.cv-picker__model');
      let hidden = 0;
      if (!panel.classList.contains('is-expanded')) {
        items.forEach((item) => {
          if (item.offsetParent === null) hidden += 1;
        });
      }
      // Enter right after the last visible tile in the stagger.
      button.parentElement.style.setProperty('--i', String(items.length - hidden));
      // Not worth a button: on phones it is a full row (so hide at least 2 models);
      // on wider screens it is a tile that takes a cell itself (so hide at least 3).
      if (hidden > 0 && hidden <= (tileMode.matches ? 2 : 1)) {
        panel.classList.add('is-expanded');
        panel.dataset.moreAuto = 'true';
        hidden = 0;
      }
      const label = button.querySelector('.cv-picker__more-label');
      const template = this.dataset.moreLabel || '';
      if (label && hidden > 0 && template) label.textContent = template.replace('[count]', countText(hidden));
      const on = hidden > 0 && Boolean(template);
      button.hidden = !on;
      button.setAttribute('aria-expanded', 'false');
      button.parentElement.classList.toggle('is-on', on);
    }

    get animate() {
      return !reduceMotion.matches && !document.body.classList.contains('disable-animations');
    }

    select(index, { focus = false, animate = true, clearSearch = false, remember = true } = {}) {
      if (index < 0 || index >= this.tabs.length) return;
      if (clearSearch) this.clearSearch(false);
      const previous = this.current;
      this.current = index;

      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        tab.classList.toggle('is-active', on);
      });

      this.panels.forEach((panel, i) => {
        if (!panel) return;
        const on = i === index;
        panel.classList.toggle('is-active', on);
        panel.hidden = !on;
        panel.classList.remove('is-entering');
      });

      const panel = this.panels[index];
      if (panel && animate && this.animate && previous !== index) {
        void panel.offsetWidth; // restart the entrance animation
        panel.classList.add('is-entering');
      }

      if (focus) this.tabs[index].focus();
      if (remember) store.set(STORE_BRAND, this.tabs[index].dataset.brand || '');
      this.moveIndicator();
      this.revealTab(this.tabs[index]);
      this.updateMore(panel);
    }

    revealTab(tab) {
      const list = this.tablist;
      if (!list || list.scrollWidth <= list.clientWidth) return;
      const left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
      list.scrollTo({ left: Math.max(0, left), behavior: this.animate ? 'smooth' : 'auto' });
    }

    moveIndicator() {
      const tab = this.tabs[this.current];
      if (!tab || !this.indicator) return;
      this.style.setProperty('--ind-x', `${tab.offsetLeft}px`);
      this.style.setProperty('--ind-y', `${tab.offsetTop}px`);
      this.style.setProperty('--ind-w', `${tab.offsetWidth}px`);
      this.style.setProperty('--ind-h', `${tab.offsetHeight}px`);
    }

    expand(panel) {
      if (!panel) return;
      const firstHidden = Array.from(panel.querySelectorAll('.cv-picker__model')).find((item) => item.offsetParent === null);
      panel.classList.add('is-expanded');
      delete panel.dataset.moreAuto;
      const button = panel.querySelector('[data-more]');
      // Only a focused button (keyboard, or a mouse click in most browsers) needs its
      // focus handed on. A tap never focuses it (theme.js cancels touchend), and moving
      // focus then would draw a focus ring on a tile the visitor did not choose.
      const hadFocus = Boolean(button && button.contains(document.activeElement));
      if (button) {
        button.setAttribute('aria-expanded', 'true');
        button.hidden = true;
        button.parentElement.classList.remove('is-on');
      }
      // Move focus to the first model that just appeared (the button is gone).
      const next = firstHidden ? firstHidden.querySelector('.cv-picker__chip') : null;
      if (next && hadFocus) next.focus({ preventScroll: true });
    }

    onKeydown(event) {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();
      const last = this.tabs.length - 1;
      const from = this.tabs.indexOf(document.activeElement);
      const at = from > -1 ? from : this.current;
      let next = at;
      if (event.key === 'ArrowLeft') next = at === 0 ? last : at - 1;
      if (event.key === 'ArrowRight') next = at === last ? 0 : at + 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = last;
      this.select(next, { focus: true, clearSearch: true });
    }

    onInput() {
      const raw = this.input.value;
      const query = normalize(raw);
      if (this.clearBtn) this.clearBtn.hidden = raw.length === 0;
      if (!query) {
        this.exitSearch();
        return;
      }
      this.search(query);
    }

    search(query) {
      const tokens = query.split(' ');
      const flatQuery = compact(query);
      const matches = this.index.filter(
        (entry) =>
          tokens.every((token) => entry.text.includes(token)) ||
          entry.flat.includes(flatQuery) ||
          (entry.anyModel && tokens.some((token) => token.length > 2 && entry.words.some((word) => word.startsWith(token))))
      );

      this.classList.add('is-searching');
      this.panels.forEach((panel) => {
        if (panel) panel.hidden = true;
      });
      if (this.results) this.results.hidden = false;

      if (this.resultsList) {
        const fragment = document.createDocumentFragment();
        matches.forEach((entry, i) => {
          if (!entry.clone) {
            entry.clone = entry.item.cloneNode(true);
            entry.clone.removeAttribute('data-shopify-editor-block');
          }
          entry.clone.style.setProperty('--i', String(Math.min(i, 12)));
          fragment.appendChild(entry.clone);
        });
        this.resultsList.replaceChildren(fragment);
        this.resultsList.classList.remove('is-entering');
        if (this.animate) {
          void this.resultsList.offsetWidth;
          this.resultsList.classList.add('is-entering');
        }
      }

      const none = matches.length === 0;
      if (this.empty) this.empty.hidden = !none;
      if (none) this.suggestBrand(tokens);
      this.announce(matches.length);
    }

    suggestBrand(tokens) {
      if (!this.emptyBrand) return;
      const tab = this.tabs.find((t) => {
        const words = normalize(t.dataset.keywords).split(' ').filter((w) => w.length > 1);
        return tokens.some((token) => token.length > 1 && words.some((w) => w.startsWith(token) || token.startsWith(w)));
      });
      const panel = tab ? this.panels[this.tabs.indexOf(tab)] : null;
      const link = panel ? panel.querySelector('.cv-picker__all[href]') : null;
      if (tab && link) {
        this.emptyBrand.href = link.getAttribute('href');
        this.emptyBrandLabel.textContent = `${this.dataset.brandLinkPrefix || ''} ${tab.dataset.brand}`.trim();
        this.emptyBrand.hidden = false;
      } else {
        this.emptyBrand.hidden = true;
      }
    }

    announce(count) {
      if (!this.status) return;
      let text = '';
      if (count === 1) {
        text = this.dataset.resultsOne || '';
      } else {
        text = (this.dataset.resultsMany || '').replace('[count]', countText(count)).replace(/\bde de\b/, 'de');
      }
      this.status.textContent = text;
    }

    exitSearch() {
      this.classList.remove('is-searching');
      if (this.results) this.results.hidden = true;
      if (this.resultsList) this.resultsList.replaceChildren();
      if (this.status) this.status.textContent = '';
      this.panels.forEach((panel, i) => {
        if (panel) panel.hidden = i !== this.current;
      });
      this.moveIndicator();
      this.updateMore(this.panels[this.current]);
    }

    clearSearch(focusInput) {
      if (!this.input) return;
      const had = this.input.value.length > 0;
      this.input.value = '';
      if (this.clearBtn) this.clearBtn.hidden = true;
      if (had || this.classList.contains('is-searching')) this.exitSearch();
      if (focusInput) this.input.focus();
    }
  }

  customElements.define('cv-device-picker', CvDevicePicker);
})();
